import { useId, useLayoutEffect, useRef, type InputHTMLAttributes, type ReactElement, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { Select as Choice } from '@beyond-js/ui';
import { Field as Family, Icon } from '@beyond-js/ui/react';

interface FieldProperties {
	label: string;
	hint?: ReactNode;
	/** Localized validation message; marks the control invalid and describes it */
	error?: string;
	children: (control: { id: string; 'aria-describedby': string | undefined; 'aria-invalid': true | undefined }) => ReactElement;
}

/**
 * The family's labelled field from `@beyond-js/ui`: label, hint, control and error, with the hint and the error
 * describing the control and an error marking it invalid. The control is given its identifier through the
 * function this application's screens pass, and the family's field adds the rest.
 */
export function Field({ label, hint, error, children }: FieldProperties) {
	const id = useId();
	return (
		<Family label={label} hint={hint} error={error}>
			{children({ id, 'aria-describedby': undefined, 'aria-invalid': error ? true : undefined })}
		</Family>
	);
}

export function TextInput({ className = '', ...rest }: InputHTMLAttributes<HTMLInputElement>) {
	return <input {...rest} className={`bui-input ${className}`} />;
}

export function TextArea({ className = '', rows = 3, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
	return <textarea {...rest} rows={rows} className={`bui-input bui-textarea ${className}`} />;
}

/**
 * The family's native select: the options are this screen's own `<option>` elements. A name the select cuts
 * (an application's title, a graph's identifier) shows whole on hover and on keyboard focus, and only while it is
 * cut (D44), through the family's `Select.tip`; the opened list shows every option whole.
 */
export function Select({ className = '', children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
	const control = useRef<HTMLSelectElement>(null);
	useLayoutEffect(() => {
		if (!control.current) return undefined;
		const tip = Choice.tip(control.current);
		return () => tip.destroy();
	}, []);
	return (
		<span className="bui-select w-full">
			<select {...rest} ref={control} className={`bui-select-control ${className}`}>
				{children}
			</select>
			<Icon name="chevron" size={16} />
		</span>
	);
}

/**
 * One checkbox with its label and an optional note, as the family's choices draw it.
 */
export function Checkbox({ label, hint, disabled, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode }) {
	const id = useId();
	return (
		<div className={`bui-choice${disabled ? ' bui-choice-disabled' : ''}`}>
			<input {...rest} id={id} type="checkbox" disabled={disabled} aria-describedby={hint ? `${id}-hint` : undefined} />
			<label htmlFor={id}>{label}</label>
			{hint && (
				<p id={`${id}-hint`} className="bui-choice-note">
					{hint}
				</p>
			)}
		</div>
	);
}
